import * as Yup from "yup";
import { FormGroup } from "../../../components/form/models/models";
import { LGA } from "../../utils/LGA";
import { maxDob, minDob } from "../../utils/childDobRange";

export const applicationCreationForm: FormGroup = {
  type: "formGroup",
  fields: [
    {
      type: "field",
      key: "firstName",
      fieldType: "input",
      label: "Child's given name",
      props: {
        isRequired: true,
      },
      validation: Yup.string()
        .trim()
        .required("A response is required") // Basic required validation
        .matches(RegExp("^(?! )[A-Za-z' -]+(?<! )$"), {
          message: "Name may only use alphabetic (A-Z) characters and hyphen (-) and apostrophe (')",
        }),
    },
    {
      type: "field",
      key: "lastName",
      fieldType: "input",
      label: "Child's family name",
      props: {
        isRequired: true,
      },
      validation: Yup.string()
        .trim()
        .required("A response is required") // Basic required validation
        .matches(RegExp("^(?! )[A-Za-z' -]+(?<! )$"), {
          message: "Name may only use alphabetic (A-Z) characters and hyphen (-) and apostrophe (')",
        }),
    },
    {
      type: "field",
      key: "dateOfBirth",
      fieldType: "calendar",
      label: "Child's date of birth",
      props: {
        isRequired: true,
        extendedlabel:
          "If the child is over six years old, please ensure that if a school exemption is required that it has been submitted to the DE regional office. This application cannot be assessed until this has occurred",
      },
      validation: Yup.date()
        .required("A response is required")
        .min(minDob, "Child may not be more than seven (7) years old")
        .max(maxDob, "Child must be at least two (2) years old"),
    },
    {
      type: "field",
      key: "gender",
      fieldType: "dropdown",
      label: "Gender",
      props: {
        isRequired: true,
        placeholder: "Select a Gender",
        options: [
          {
            label: "Female",
            value: 1,
          },
          {
            label: "Male",
            value: 2,
          },
          {
            label: "Other",
            value: 3,
          },
        ],
      },
      validation: Yup.string().trim().required("A response is required"),
    },
    {
      type: "field",
      key: "kindergartenLGA",
      fieldType: "dropdown",
      label: "LGA the kindergarten is located in",
      props: {
        isRequired: true,
        options: LGA,
        Body: () => (
          <span className="m-0">
            You can search for the kindergarten's LGA by using the Victorian government's 'Know Your Council' page (link:{" "}
            <a href="https://www.vic.gov.au/know-your-council" target="_blank" rel="noreferrer" className="underline">
              https://www.vic.gov.au/know-your-council
            </a>
            <i className="pi pi-external-link"></i>)
          </span>
        ),
      },
      validation: Yup.string().trim().required("A response is required"),
    },
    {
      type: "field",
      key: "fundedKinder",
      fieldType: "search",
      label: "Name of the kindergarten service lodging this application",
      expressionProps: {
        disabled: (context, values, model) => model.kindergartenLGA == null || model.kindergartenLGA == "" || context.disabled,
        customParams: (context, values, model) => {
          return { LGA: model.kindergartenLGA };
        },
      },
      extendedlabel: (
        <p className="list-none pl-2 m-0">
          You must record the LGA the kindergarten is located in before completing this field.
          <br /> <br />
          Enter the first few letters of your kindergarten name to search. If you cannot find the name of the kindergarten in the list, select 'Other'
          and the "Enter the Name of the Kindergarten" field will display. Enter the name of the kindergarten in that field.
        </p>
      ),
      props: {
        apiPath: "FundedKinder/search",
        placeholder: "Kindergarten Name",
        labelValue: "accountName",
        noResultsMessage: "The kindergarten service is not listed",
        displayField: "accountName",
        isRequired: true,
      },
      validation: Yup.object().required("A response is required."),
    },
    {
      type: "field",
      fieldType: "input",
      key: "kindergartenNameOther",
      label: "Please enter the name of the kindergarten service",
      props: {
        isRequired: true,
      },
      validation: Yup.string().trim().required("A response is required"),
      hideExpression: (context, values, model) => {
        return model?.fundedKinder?.id !== -1;
      },
    },
    {
      type: "field",
      key: "serviceApprovalNumber",
      fieldType: "input",
      label: "What is the service approval (SE) number of your kindergarten?",
      extendedlabel: (
        <div className="service-screening">
          {" "}
          <ul className="list-none pl-2 m-0">
            This is the official service approval number assigned to your kindergarten under the National Quality Framework. It typically begins with
            <strong> SE-</strong> followed by eight digits (e.g. SE-12345678).
            <br /> <br />
            You can find this number by searching your service name on the{" "}
            <a
              className="underline"
              href="https://www.acecqa.gov.au/resources/national-registers/services?s=&f%5B0%5D=service_state%3AVIC"
              target="_blank"
              rel="noreferrer">
              ACECQA National Register
            </a>
            .<i className="pi pi-external-link"></i>
            <br /> <br />
            Alternatively in Arrival:
            <li>Go to the Services tab</li>
            <li>Search for the service name</li>
            <li>The SE number (NQAITS ID) appears in the second column of the search results</li>
            <li>
              Alternatively, in the actions columns click the eye icon to open the service record, then under "Service details" the third row should
              be the SE-number
            </li>
          </ul>
        </div>
      ),
      validation: Yup.string().notRequired().trim().matches(RegExp("^SE-\\d{8}$"), { message: "A valid SE number is required" }),
    },
    {
      type: "field",
      key: "contactNumber",
      fieldType: "input",
      label: "Phone Number",
      props: {
        isRequired: true,
      },
      validation: Yup.string()
        .trim()
        .required("A response is required")
        .matches(
          RegExp(
            "^((04\\d{2}\\s?\\d{3}\\s?\\d{3})|(\\+614\\s?\\d{4}\\s?\\d{4})|((\\(02\\)|\\(03\\)|\\(08\\))\\s?\\d{4}\\s?\\d{4})|(02|03|08)\\s?\\d{4}\\s?\\d{4}|(\\+613\\s?\\d{4}\\s?\\d{4})|(\\+612\\s?\\d{4}\\s?\\d{4})|(\\+618\\s?\\d{4}\\s?\\d{4}))$"
          ),
          {
            message: "Please enter a valid contact number",
          }
        ),
    },
  ],
};
